import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageRange from '../../common/components/PageRange';
import Pagination from '../../common/components/Pagination';
import SHRMMessage from '../../common/components/SHRMMessage';

import { useQuery } from '../../common/helpers';
import { useAppSelector } from '../../common/hooks';
import { BOOKMARKS_PAGE_SIZE } from '../../constants';
import { IBookmarksContainer } from '../../models/Bookmarks/IBookmarksContainer';
import { IBookmarkUpdate } from '../../models/Bookmarks/IBookmarkUpdate';
import { BookmarkSortingType, BookmarkType, UserStatus } from '../../models/enums';
import { RootState } from '../../redux/store';
import { deleteBookmark, getBookmarksPaged, handleErrorResponse, updateNote } from '../../services';
import { ReactComponent as BookmarkIcon } from './assets/bookmark.svg';
import { ReactComponent as PenIcon } from './assets/pen.svg';
import { ReactComponent as WrenchIcon } from './assets/wrench.svg';
import styles from './BookmarksPage.module.css';
import BookmarkCard from './components/BookmarkCard';
import ExtendedSelect from './components/ExtendedSelect';
import LoadingSpinner from '../../common/components/LoadingSpinner';

const BookmarksPage = () => {
  const pageSize = BOOKMARKS_PAGE_SIZE;
  const [bookmarksData, setBookmarksData] = useState<IBookmarksContainer>(
    {} as IBookmarksContainer
  );
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [ariaPressed, setAriaPressed] = useState(false);
  const [popupMesage, setPopupMessage] = useState('');
  const [orderBy, setOrderBy] = useState(BookmarkSortingType.RecentlyAdded.toString());
  const [showPopupMessage, setShowPopupMessage] = useState(false);
  const userStatus: UserStatus = useAppSelector(
    (state: RootState) => state.accountReducer.userStatus
  );
  const navigate = useNavigate();
  const pageNumber = useQuery().get('pageNumber');

  const pageStatus = () => {
    const firstItem = (currentPage - 1) * pageSize + 1;
    const max = currentPage * pageSize;
    const lastItem = bookmarksData?.totalCount < max ? bookmarksData?.totalCount : max;
    if (lastItem === firstItem) {
      return firstItem.toString();
    } else {
      return firstItem + ' - ' + lastItem;
    }
  };

  const profileContainer = useAppSelector(
    (state: RootState) => state.profileReducer.profileContainer
  );

  const setPage = () => {
    if (pageNumber) {
      setCurrentPage(+pageNumber);
    } else {
      navigate('/bookmarks?pageNumber=1', { replace: true });
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    getBookmarksPaged(
      profileContainer?.profileModel?.masterCustomerId,
      ariaPressed ? BookmarkType.ToolBookmark : BookmarkType.AllBookmarks,
      +orderBy,
      pageSize,
      currentPage
    )
      .then((response: any) => {
        setBookmarksData(response.data);
      })
      .catch((_error: any) => {
        handleErrorResponse(_error);
      })
      .finally(() => {
        setIsLoading(false);
        setPage();
      });
  }, [currentPage, ariaPressed, orderBy]);

  useEffect(() => {
    if (pageNumber && currentPage && +pageNumber !== currentPage)
      navigate(`/bookmarks?pageNumber=${currentPage}`);
  }, [pageNumber]);

  const changeCurrentPage = (pageNumber: number) => {
    navigate(`/bookmarks?pageNumber=${pageNumber}`);
    setCurrentPage(pageNumber);
  };

  const handleBookmarkDelete = (bookmarkId: number) => {
    deleteBookmark(profileContainer?.profileModel?.masterCustomerId, bookmarkId)
      .then((_response: any) => {
        return getBookmarksPaged(
          profileContainer?.profileModel?.masterCustomerId,
          ariaPressed ? BookmarkType.ToolBookmark : BookmarkType.AllBookmarks,
          +orderBy,
          pageSize,
          currentPage
        );
      })
      .then((_response: any) => {
        setBookmarksData(_response.data);
        setPage();
        setPopupMessage('✔︎ Bookmark removed.');
        setShowPopupMessage(true);
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      })
      .catch((_error: any) => {
        handleErrorResponse(_error);
      });
  };

  const handleNotesChange = (bookmarkId: number, bookmarkNotes: string) => {
    const bookmark: IBookmarkUpdate = {
      bookmarkId: bookmarkId,
      ssoUUId: profileContainer?.profileModel?.masterCustomerId,
      notes: bookmarkNotes,
    };

    updateNote(bookmark)
      .then((_response: any) => {
        setBookmarksData(_response.data);
        setPage();
        setPopupMessage('✔︎ Notes updated.');
        setShowPopupMessage(true);
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      })
      .catch((_error: any) => {
        handleErrorResponse(_error);
      });
  };

  const handleToggleButton = () => {
    !ariaPressed ? setAriaPressed(true) : setAriaPressed(false);
  };

  const handleChangeOption = (value: string) => {
    setOrderBy(value);
  };

  if (
    userStatus === UserStatus.GracePeriodMember ||
    userStatus === UserStatus.ExpiringMember ||
    userStatus === UserStatus.Member
  ) {
    return (
      <React.Fragment>
        <div className={styles.container}>
          {isLoading ? (
            <div className={styles.center}>
              <LoadingSpinner />
            </div>
          ) : (
            <React.Fragment>
              <div>
                <div>
                  <SHRMMessage
                    color='var(--shrmGreen)'
                    text={popupMesage}
                    showMessage={showPopupMessage}
                  />
                  <div className={styles.numberingVisible}>
                    <PageRange
                      currentPageRange={pageStatus()}
                      totalItems={bookmarksData?.totalCount}
                    />
                  </div>
                  <div className={styles.navigation}>
                    <ExtendedSelect
                      propStyle={styles.dropdown}
                      label='SORT BY:'
                      options={[
                        { value: '1', name: 'Recently Added' },
                        { value: '2', name: 'Title' },
                        { value: '3', name: 'Newest' },
                        { value: '4', name: 'Oldest' },
                      ]}
                      handleChangeOption={handleChangeOption}
                      selectedOption={orderBy}
                    />
                    <div className={styles.numbering}>
                      <PageRange
                        currentPageRange={pageStatus()}
                        totalItems={bookmarksData?.totalCount}
                      />
                    </div>
                    <div className={styles.pagination}>
                      <Pagination
                        itemsPerPage={pageSize}
                        totalItems={bookmarksData?.totalCount}
                        totalPages={bookmarksData?.totalPages}
                        changePage={changeCurrentPage}
                        currentPage={currentPage}
                      />
                    </div>
                  </div>
                </div>
                <div className={styles.bookmarksPanel}>
                  <header className={styles.bookmarksPanelHeader}>
                    <div className={styles.headerToggle}>
                      <button
                        className={styles.toggleButtonAllBookmarks}
                        onClick={handleToggleButton}
                        type='button'
                        aria-pressed={!ariaPressed}
                      >
                        <BookmarkIcon className={styles.bookmarkIcon} />
                        ALL
                      </button>
                      <button
                        className={styles.toggleButtonToolsOnly}
                        onClick={handleToggleButton}
                        type='button'
                        aria-pressed={ariaPressed}
                      >
                        <WrenchIcon className={styles.wrenchIcon} />
                        TOOLS ONLY
                      </button>
                    </div>
                  </header>
                  {bookmarksData?.bookmarks?.length === 0 ? (
                    <div className={styles.noBookMarks}>
                      {!ariaPressed ? (
                        <p>
                          <strong>You have no saved Bookmarks.</strong>
                        </p>
                      ) : (
                        <p>
                          <strong>You have no saved Tools.</strong>
                        </p>
                      )}
                      <p>
                        To save a page to your bookmarks simply select &nbsp;
                        <BookmarkIcon className={styles.icon} /> &nbsp; button on the article page.
                      </p>
                      <p>
                        Once you have saved your bookmarks, you can &nbsp;
                        <PenIcon className={styles.icon} /> &nbsp; add notes.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className={styles.bookmarksCards}>
                        {bookmarksData?.bookmarks?.map((bookmark, index) => {
                          return (
                            <BookmarkCard
                              key={index}
                              id={bookmark.bookmarkId}
                              linkUrl={bookmark.bookmarkLink}
                              link={bookmark.headline}
                              description={bookmark.subheading}
                              notes={bookmark.notes}
                              added={bookmark.addedDate}
                              handleBookmarkDelete={handleBookmarkDelete}
                              handleNotesChange={handleNotesChange}
                            />
                          );
                        })}
                      </div>
                      <div className={styles.paginationMobile}>
                        <Pagination
                          itemsPerPage={pageSize}
                          totalItems={bookmarksData?.totalCount}
                          totalPages={bookmarksData?.totalPages}
                          changePage={changeCurrentPage}
                          currentPage={currentPage}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </React.Fragment>
          )}
        </div>
      </React.Fragment>
    );
  } else {
    window.location.replace('/profile');
  }
};
export default BookmarksPage;
